import { useMemo } from 'react'

import {
  Divider,
  Header,
  IconChevronDoubleRight,
  Space,
  SubText,
  Text,
  TransparentIconButton,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import { CatalogItemDetailsGallery } from './CatalogItemDetailsGallery'
import { CatalogItemDetailsSections } from './CatalogItemDetailsSections'
import messages from './messages'
import {
  CatalogItemTagVariants,
  resolveInfoBadges,
} from '../../value-objects/CatalogItemTag'
import {
  type ConversationCatalogItemDelivery,
  type ConversationCatalogItemRating,
  type ConversationCatalogItemVendorBadge,
  type ConversationDetailSection,
  type ConversationVendor,
  type ConversationVolumePricing,
} from '../../value-objects/ConversationTurnItem'
import { CatalogItemDetailsCart } from '../CatalogItemDetailsCart'
import { CatalogItemRating } from '../CatalogItemRating'
import { CatalogItemTag, type ICatalogItemTagProps } from '../CatalogItemTag'
import { VendorLogoBadge } from '../VendorLogoBadge'
import { CatalogItemDetailsBodyStyled } from './styles/CatalogItemDetailsBodyStyled'
import { CatalogItemDetailsFooterStyled } from './styles/CatalogItemDetailsFooterStyled'
import { CatalogItemDetailsHeaderStyled } from './styles/CatalogItemDetailsHeaderStyled'
import { CatalogItemDetailsLayoutStyled } from './styles/CatalogItemDetailsLayoutStyled'
import { CatalogItemDetailsPriceStyled } from './styles/CatalogItemDetailsPriceStyled'

export interface ICatalogItemDetailsProps {
  img?: string
  images?: string[]
  title: string
  sku: string
  tag: ICatalogItemTagProps
  price: number
  currency?: CurrencyName
  vendor?: ConversationVendor
  rating?: ConversationCatalogItemRating
  vendorBadges?: ConversationCatalogItemVendorBadge[]
  listPrice?: number
  volumePricing?: ConversationVolumePricing
  sections: ConversationDetailSection[]
  delivery?: ConversationCatalogItemDelivery
  quantity: number
  estimatedTotal: number
  onQuantityChange: (quantity: number) => void
  onAddToRequest: () => void
  onChipSelect: (message: string) => void
  onCollapse: () => void
}

export const CatalogItemDetails = ({
  img,
  images,
  title,
  sku,
  tag,
  price,
  currency,
  vendor,
  rating,
  vendorBadges = [],
  listPrice,
  volumePricing,
  sections,
  delivery,
  quantity,
  estimatedTotal,
  onQuantityChange,
  onAddToRequest,
  onChipSelect,
  onCollapse,
}: ICatalogItemDetailsProps) => {
  const intl = useProcurifyIntl()

  const galleryImages = useMemo(() => {
    if (images?.length) return images
    return img ? [img] : []
  }, [images, img])

  const infoBadges = resolveInfoBadges(vendorBadges)

  const showSku = !vendor?.logoUrl

  const renderSource = () => (
    <Space direction='horizontal' size='small' align='center' flexWrap>
      {vendor?.logoUrl ? (
        <VendorLogoBadge vendor={vendor} />
      ) : (
        <CatalogItemTag icon={tag.icon} text={tag.text} />
      )}
      {infoBadges.map((badge) => (
        <CatalogItemTag
          key={badge.slot}
          icon={badge.icon}
          text={badge.text}
          logoUrl={badge.logoUrl}
          variant={badge.variant}
        />
      ))}
    </Space>
  )

  const renderPrice = () => (
    <Space direction='vertical' size='small'>
      <Space direction='horizontal' size='small' align='center'>
        <CatalogItemDetailsPriceStyled className='fs-mask'>
          <Price inline price={price} currency={currency} />
        </CatalogItemDetailsPriceStyled>
        {!!listPrice && (
          <SubText inline noWrap color='textSecondary' className='fs-mask'>
            {intl.formatMessage(messages.listPriceLabel)}{' '}
            <s>
              <Price inline price={listPrice} currency={currency} />
            </s>
          </SubText>
        )}
      </Space>
      {volumePricing?.savingsHint && (
        <Space direction='horizontal' size='xsmall' align='center'>
          <CatalogItemTag
            text={intl.formatMessage(messages.savingsHint, {
              pct: volumePricing.savingsHint.savingsPct,
              label: volumePricing.savingsHint.label,
            })}
            variant={CatalogItemTagVariants.MUTED}
          />
        </Space>
      )}
      {!!volumePricing?.tiers?.length && (
        <Space direction='vertical' size='xsmall'>
          <Text bold>{intl.formatMessage(messages.volumePricingTitle)}</Text>
          {volumePricing.tiers.map((tier) => (
            <Space key={tier.label} direction='horizontal' align='center'>
              <Space.Item grow={1}>
                <SubText color='textSecondary'>{tier.label}</SubText>
              </Space.Item>
              <Text bold className='fs-mask'>
                <Price inline price={tier.unitPrice} currency={tier.currency} />
                {intl.formatMessage(messages.perUnitSuffix)}
              </Text>
            </Space>
          ))}
        </Space>
      )}
    </Space>
  )

  return (
    <CatalogItemDetailsLayoutStyled>
      <CatalogItemDetailsHeaderStyled>
        <TransparentIconButton
          iconComponent={<IconChevronDoubleRight size={24} block aria-hidden />}
          aria-label={intl.formatMessage(messages.collapseAriaLabel)}
          analyticsId='catalog-item-details-collapse-button'
          onClick={onCollapse}
        />
      </CatalogItemDetailsHeaderStyled>

      <CatalogItemDetailsBodyStyled>
        <div>
          <Space direction='vertical' size='medium'>
            <CatalogItemDetailsGallery images={galleryImages} />
            {renderSource()}
            <Space direction='vertical' size='xsmall'>
              <Header as='h4'>
                <span className='fs-mask'>{title}</span>
              </Header>
              {rating && (
                <CatalogItemRating
                  value={rating.value}
                  count={rating.count}
                  scaleMax={rating.scaleMax}
                />
              )}
              {showSku && (
                <Text color='darkGrey' className='fs-mask'>
                  {intl.formatMessage(messages.sku, { sku })}
                </Text>
              )}
            </Space>
            <Divider />
            {renderPrice()}
            <Divider />
          </Space>

          <CatalogItemDetailsSections
            sections={sections}
            onChipSelect={onChipSelect}
          />
        </div>
      </CatalogItemDetailsBodyStyled>

      <CatalogItemDetailsFooterStyled data-testid='catalog-item-details-footer'>
        <CatalogItemDetailsCart
          quantity={quantity}
          estimatedTotal={estimatedTotal}
          currency={currency}
          delivery={delivery}
          onQuantityChange={onQuantityChange}
          onAddToRequest={onAddToRequest}
        />
      </CatalogItemDetailsFooterStyled>
    </CatalogItemDetailsLayoutStyled>
  )
}
